import React from 'react'
import moment from 'moment'
import { DateRangePicker } from '../../_library/DateRangePicker'
import LoadingBar from '../../_library/LoadingBar'
import SortableTable from '../../_library/SortableTable/index'
import EmptyBar from '../../_library/EmptyBar'
import { getTableColumns } from '../../utils/sortableTableUtils'
import { formatDay, formatDateTime, defaultDateFormat } from '../../../_common/core/validation/normalizers'
import { SaveTemplate, SelectFilterTemplate } from '../../_library/FilterTemplate'
import CardHeader from '../../_library/CardHeader'
import TrafficViewChart from './components/TrafficViewChart'
import _groupBy from 'lodash/groupBy'
import _map from 'lodash/map'
import _isEqual from 'lodash/isEqual'
import _uniqBy from 'lodash/uniqBy'
import { showAxiosError } from '../../utils/messenger'
import { DAY_DISPLAY_FORMAT } from '../../constants/timeFormats'
import ProgressCircle from '../../_library/ProgressCircle'

export default class TrafficViews extends React.PureComponent {
  constructor(props) {
    super(props)
    this.tableColumns = getTableColumns([
      {
        key: 'created_at',
        label: 'View Date',
        isSortable: true,
        normalizer: formatDay,
      },
      {
        key: 'total',
        label: 'No. of Visitors',
        isSortable: true,
      },
      {
        key: 'conversion_visitors',
        label: 'No. of Conversions',
        isSortable: true,
      },
    ])

    this.detailsTableColumns = getTableColumns([
      {
        key: 'created_at',
        label: 'View Hour',
        className: 'detail-cell',
        isSortable: true,
        normalizer: formatDateTime,
      },
      {
        key: 'total',
        label: 'No. of Visitors',
        className: 'detail-cell',
        isSortable: true,
      },
      {
        key: 'conversion_visitors',
        label: 'No. of Conversions',
        className: 'detail-cell',
        isSortable: true,
      },
    ])

    this.footbarColumns = [
      { key: 'total', sourceKey: 'total' },
      { key: 'conversion_visitors', sourceKey: 'conversion_visitors' },
    ]

    this.state = {
      sortBy: { column: 'created_at', asc: false },
      sortBySubTable: { column: 'created_at', asc: true },
      filterUpdateVersion: 0,
      unique: [],
      hourly: [],
    }
  }

  componentDidMount() {
    this.calculateTrafficData()
  }

  componentDidUpdate(prevProps) {
    const { trafficDataObj } = this.props

    if (!_isEqual(trafficDataObj, prevProps.trafficDataObj)) {
      this.calculateTrafficData()
    }
  }

  calculateTrafficData = () => {
    const { trafficDataObj, params } = this.props

    const [time] = params[0].group_by

    const uniqueRaw = [...(trafficDataObj[`confirmation_${time}_unique`] ?? [])]
    const conversions = [...(trafficDataObj.confirmation_hour_unique ?? [])]

    const hourly = [...(trafficDataObj.event_hour_unique ?? [])]

    const unique = _map(
      _groupBy(uniqueRaw, row =>
        time === 'week'
          ? moment(row.created_at).startOf('isoWeek').format(DAY_DISPLAY_FORMAT) +
            ' - ' +
            moment(row.created_at).endOf('isoWeek').format(DAY_DISPLAY_FORMAT)
          : moment(row.created_at).format(
              time === 'hour'
                ? DAY_DISPLAY_FORMAT + ' h:[00] a'
                : time === 'month'
                ? 'MMMM YYYY'
                : DAY_DISPLAY_FORMAT,
            ),
      ),
      rows => {
        rows[0].total = rows.length
        return rows[0]
      },
    )

    let converted = 0
    let visitors = 0
    hourly.push(...conversions)
    const hourlyWithConversions = _map(
      _groupBy(
        _uniqBy(hourly, ({ id }) => id),
        row => moment(row.created_at, defaultDateFormat).format('YYYY-MM-DD h:[00] a'),
      ),
      hourList => {
        const [hourRow] = hourList
        let conversion_visitors = 0
        let total = 0
        for (const idx in hourList) {
          if (hourList[idx].page === 'EVENT' || hourList[idx].page === 'PRE_REGISTRATION') {
            total++
            visitors++
          } else {
            conversion_visitors++
            converted++
          }
        }
        hourRow.total = total
        hourRow.conversion_visitors = conversion_visitors
        return hourRow
      },
    )

    this.setState({
      unique,
      hourly: hourlyWithConversions,
      converted,
      notConverted: visitors - converted,
    })
  }

  changeTime = time => {
    const { params, setTrafficDataParams } = this.props
    const [firstParam, ...rest] = params
    firstParam.group_by = [time, 'unique']
    setTrafficDataParams([firstParam, ...rest])
  }

  isEmptyChecker = data => {
    const { startDate, endDate } = data
    return !(startDate && endDate)
  }

  onSaveFilter = () => {
    this.setState(s => ({ filterUpdateVersion: s.filterUpdateVersion + 1 }))
  }

  onSelectFilter = template => {
    const { onDatesApply } = this.props

    let { value } = template
    try {
      value = JSON.parse(value)
      const { startDate, endDate } = value
      onDatesApply(moment(startDate), moment(endDate))
    } catch (e) {
      showAxiosError(e)
    }
  }

  getPercentage = (total, value) => (Boolean(total) ? Math.round((value * 100) / total) : 0)

  render() {
    const { event, isLoadingTrafficData, startDate, endDate, params, onDatesApply } = this.props
    const { sortBy, filterUpdateVersion, unique, hourly, converted, notConverted } = this.state

    const showSaveTemplateBtn = startDate && endDate
    const countState = hourly.length
    const total = notConverted + converted
    const convertedPercent = this.getPercentage(total, converted)
    const notConvertedPercent = this.getPercentage(total, notConverted)

    const days = hourly.reduce((curr, { total, conversion_visitors, created_at }) => {
      const date = moment(created_at).format('YYYY-MM-DD')
      const index = curr.findIndex(item => item.id === date)
      if (index !== -1) {
        curr[index].total += Number(total)
        curr[index].conversion_visitors += Number(conversion_visitors)
        return curr
      }
      curr.push({ id: date, total, conversion_visitors, created_at })
      return curr
    }, [])

    const detailsRows = Object.entries(
      _groupBy(hourly, hourRow => moment(hourRow.created_at).format('YYYY-MM-DD')),
    ).map(keyValue => ({
      id: keyValue[0],
      type: 'detailRow',
      component: (
        <SortableTable
          data={keyValue[1]}
          tableColumns={this.detailsTableColumns}
          enableSort={true}
          enableCopyTable={true}
          disableMobileView={true}
          className="child-table"
          sortBy={this.state.sortBySubTable}
        />
      ),
    }))

    return (
      <div ref="cardContainer" className="performance-sales traffic-conversions">
        <CardHeader
          imageUrl="/resources/images/event/performance/ticket-sales-ico.svg"
          bodyText="Conversions"
        />
        <div className="table-background">
          {isLoadingTrafficData ? (
            <LoadingBar title={"Hold tight! We're getting your statistics..."} />
          ) : countState === 0 ? (
            <EmptyBar />
          ) : (
            <div>
              <TrafficViewChart
                traffic={unique}
                changeTime={this.changeTime}
                time={params[0].group_by[0]}
                isConversions
              />
              {countState !== 0 ? (
                <div className="event-traffic-circles">
                  <div style={{ width: '600px' }} className="event-traffic-circle conversions">
                    <div className="col-xs-12 col-sm-4 blue-circle">
                      <ProgressCircle value={convertedPercent} />
                      <div className="circle-info">
                        <div className="circle-description">
                          <div className="description1">
                            <div className="circle-value">{converted}</div>
                            <div className="circle-type">Converted</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-xs-12 col-sm-4 pink-circle">
                      <ProgressCircle value={notConvertedPercent} />
                      <div className="circle-info">
                        <div className="circle-description">
                          <div className="description1">
                            <div className="circle-value">{notConverted}</div>
                            <div className="circle-type">Not Converted</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              {countState > 0 && (
                <div className="row performance-sales-daterange">
                  <div className="col-sm-8 col-xs-12">
                    <div
                      style={{ display: 'inline-block', marginTop: 10, marginBottom: 10, marginRight: 10 }}
                    >
                      <DateRangePicker
                        onApply={onDatesApply}
                        onClear={onDatesApply}
                        value={{ startDate, endDate }}
                      />
                    </div>
                    {showSaveTemplateBtn ? (
                      <div style={{ margin: '10px 0px', display: 'inline-block' }}>
                        <SaveTemplate
                          data={{
                            startDate,
                            endDate,
                          }}
                          isEmptyChecker={this.isEmptyChecker}
                          storagePath={`event-${event.id}-performance-ticket-sales-filter`}
                          onSaveFilter={this.onSaveFilter}
                        />
                      </div>
                    ) : null}
                  </div>
                  <div
                    className="col-sm-4 col-xs-12"
                    style={{ margin: '10px 0', textAlign: isMobileDevice() ? 'start' : 'end' }}
                  >
                    <SelectFilterTemplate
                      storagePath={`event-${event.id}-performance-ticket-sales-filter`}
                      onSelectFilter={this.onSelectFilter}
                      updateVersion={filterUpdateVersion}
                    />
                  </div>
                </div>
              )}
              <SortableTable
                data={days}
                tableColumns={this.tableColumns}
                enableSort={true}
                enableCopyTable={true}
                disableMobileView={true}
                sortBy={sortBy}
                detailsRows={detailsRows}
                footbar={{
                  label: 'Total',
                  columns: this.footbarColumns,
                }}
                scrollToRowEnabled={true}
              />
            </div>
          )}
        </div>
      </div>
    )
  }
}
